import * as R from "ramda";

import { createStore } from "@stencil/store";

import { Navigation } from "../services/Navigation/Navigation.interface";
import { defaulteDeviceState, DeviceState } from "./device/state";
import { defaultState as defaultErrorState, ErrorState } from "./error/state";
import { defaultNavigationState } from "./navigation/state";
import { detailedDiff } from "deep-object-diff";
import { logger } from "../utils/logger";
import { BehaviorSubject } from "rxjs";
import { Views, defaultSiteViewsState } from "./views/view";
import { APP_CONFIG } from "../config";

type Mapped<T> = ReadonlyMap<string, T>;
type View<T> = Readonly<T>;

interface AppState {
  DEVICE: Readonly<DeviceState>;
  VIEWS: Readonly<Views>;
  ERROR: Readonly<ErrorState>;
  NAVIGATION: Readonly<Navigation>;
}

type STATES = keyof AppState;

function getInitialState(): AppState {
  return {
    ERROR: defaultErrorState,
    DEVICE: defaulteDeviceState,
    VIEWS: { ...defaultSiteViewsState },
    NAVIGATION: defaultNavigationState,
  };
}

function logStateUpdate(updatedState: STATES, newValue: any, oldValue: any) {
  // Suppress noisy state logging unless explicitly in debug env.
  if (APP_CONFIG.environment !== ("debug" as any)) {
    return;
  }

  if (updatedState === "ERROR") {
    logger.info(
      `%c STATE: ${updatedState} - %c WILL UPDATE `,
      "color:white; font-weight:bold; background-color: red",
      "color:white; background-color: gray; font-weight: bold"
    );
    logger.info(oldValue);
  } else {
    logger.info(
      `%c STATE: ${updatedState} - %c WILL UPDATE `,
      "color:white; font-weight:bold; background-color: blue",
      "color:white; background-color: green; font-weight: bold"
    );
    logger.info(oldValue);
  }
  const stateDiff = detailedDiff(oldValue, newValue);
  const addedKeys = R.keys(stateDiff["added"]);
  const deletedKeys = R.keys(stateDiff["deleted"]);
  const updatedKeys = R.keys(stateDiff["updated"]);

  if (addedKeys?.length > 0) {
    for (let addedKey of addedKeys) {
      logger.info(
        `%c ADDED: %c ${addedKey as string} `,
        "color:white; font-weight:bold; background-color: green",
        "color:white; font-weight:bold; background-color: grey"
      );
      console.table(stateDiff["added"][addedKey]);
    }
  }
  if (updatedKeys?.length > 0) {
    for (let updateKey of updatedKeys) {
      const updatedProps = R.keys(stateDiff['updated'][updateKey]);
      const oldValueChanged = R.pick(updatedProps, oldValue[updateKey] ?? {});
      logger.info(
        `%c UPDATED: %c ${updateKey as string} `,
        "color:white; font-weight:bold; background-color: orange",
        "color:white; font-weight:bold; background-color: grey"
      );
      logger.info(
        `%c Previous:`,
        "color:white; font-weight:bold; background-color: lightCoral"
      );
      console.table(oldValueChanged);
      logger.info(
        `%c Next:`,
        "color:white; font-weight:bold; background-color: lightGreen"
      );
      console.table(stateDiff["updated"][updateKey]);
    }
  }

  if (deletedKeys?.length > 0) {
    for (let deleteKey of addedKeys) {
      logger.info(
        `%c DELETED: %c ${deleteKey as string} `,
        "color:red; font-weight:bold; background-color: lightgray",
        "color:white; font-weight:bold; background-color: grey"
      );
      console.table(stateDiff["deleted"][deleteKey]);
    }
  }
}

const shouldUpdate = (
  newValue: Readonly<Object>,
  oldValue: Readonly<Object>,
  propChanged: STATES
): boolean => {
  if (R.equals(oldValue, newValue)) {
    logger.info(
      `%c STATE: ${propChanged} - %c UPDATE SKIPPED - %c Values unchanged `,
      "color:white; font-weight:bold; background-color: blue",
      "color:black; background-color: yellow",
      "color:white; background-color: gray",
      { [propChanged]: oldValue }
    );
    return false;
  } else {
    logStateUpdate(propChanged, newValue, oldValue);
    return true;
  }
};

const { state, onChange, reset } = createStore<AppState>(
  getInitialState(),
  shouldUpdate
);

function setState(
  stateName: keyof AppState,
  data: Partial<AppState>,
  effect: string
) {
  if (logger.getLevel() <= logger.levels.INFO) {
    logger.info("Change origin: " + effect);
  }
  state[stateName] = R.clone(data[stateName] as (Readonly<AppState['VIEWS']> & Readonly<AppState['ERROR']> & Readonly<AppState['NAVIGATION']> & Readonly<AppState['DEVICE']>));
};

function onStateKeyChange<K extends keyof AppState>(targetState: K): BehaviorSubject<AppState[K]> {
  const defaultValue = state[targetState];
  const stateSubject = new BehaviorSubject<AppState[K]>(defaultValue);
  onChange(targetState, async (newState) => {
    const previousState = stateSubject.getValue();
    if (previousState !== newState) {
      stateSubject.next(newState);
    }
  });
  return stateSubject;
}


export { state, onStateKeyChange, setState, STATES, Mapped, View, reset as resetStore };
